/**
 * The number on the sidebar's Queue row: reply drafts waiting for this person
 * plus campaign messages waiting for a yes, for the brands asked about.
 *
 * Ported from radar `src/components/review/useReviewCount.ts`. It asked once
 * a minute and kept its last answer in a module variable, with a window event
 * (`announceReviewCount`) so the Review page could move the number the moment
 * something was approved there. Here the answer lives in the query cache under
 * one key per brand choice, so the sidebar and the page read the same entry,
 * and a page that knows the count changed refreshes it with
 * `refreshReviewCount()` (or writes it with `setReviewCount`) instead of
 * dispatching an event.
 *
 * Undefined until the first answer arrives or when it could not be read, so
 * the sidebar draws no badge rather than a zero it does not know.
 */
import { useQuery } from '@tanstack/react-query'
import { queryClient } from '../../../lib/queryClient'
import { prospectingApi, prospectingKey, prospectingRetry } from '../shared/api'

/** The cache key for the count of one brand choice (`all` or `a,b`). */
export function reviewCountKey(brandParam: string): readonly unknown[] {
  return prospectingKey('/review', { brand: brandParam, for: 'badge' })
}

export function useReviewCount(brandParam: string, options: { enabled?: boolean } = {}): number | undefined {
  const query = useQuery({
    queryKey: reviewCountKey(brandParam),
    queryFn: async ({ signal }) => {
      const answer = await prospectingApi<{ badge?: number }>('/review', { params: { brand: brandParam, for: 'badge' }, signal })
      return Number(answer.badge ?? 0)
    },
    enabled: options.enabled ?? true,
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: prospectingRetry,
  })
  return query.data
}

/** A page that has just changed what waits says so, and every reader of the count moves with it. */
export function setReviewCount(brandParam: string, count: number): void {
  queryClient.setQueryData(reviewCountKey(brandParam), count)
}

/** Read every count again, whichever brands it was asked for. */
export function refreshReviewCount(): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: prospectingKey('/review'), predicate: (query) => (query.queryKey[2] as { for?: string } | undefined)?.for === 'badge' })
}
